import { Add, Delete } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
// Embedded, client-side, inline-editable grid backing a Formik field. ServerDataGrid wraps
// server pagination/sort and doesn't expose processRowUpdate / renderEditCell.
// eslint-disable-next-line no-restricted-imports
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';

import { CellAutocomplete } from '@sophocles/data-grids/components/cells';

/**
 * Editable Bill-of-Materials matrix for a Product, backed by DataGridPro.
 *
 * Props:
 *   value         – array of { id, quantity } from Formik (`values.components`).
 *   onChange      – Formik setter; called with the next array on every change.
 *   loadOptions   – async (search) => product rows; the Component autocomplete
 *                   searches the server per keystroke, so the full catalog is
 *                   reachable regardless of list caps.
 *   knownProducts – product rows ({ id, item_part_num, item_name,
 *                   item_description }) for components already saved on the
 *                   product, so existing rows label themselves without a
 *                   catalog fetch.
 *
 * Add a row by clicking the bottom "+ Add component" row (no separate
 * button). Each row's Component cell opens an Autocomplete on edit; the
 * Quantity cell is a number input. Already-selected products are disabled
 * in the option list so duplicates aren't picked, and option rows highlight
 * the matched search text.
 */
function ComponentMatrix({ value = [], onChange, loadOptions, knownProducts = [] }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();

  // Products picked in this session. Search options are transient (replaced
  // on every keystroke), so a picked row's labeling data is kept here.
  const [pickedProducts, setPickedProducts] = useState({});

  // Per-row stable id. Components don't have a unique key of their own
  // (they're flattened to { id, quantity }), so we keep a parallel array of
  // tempIds that grows/shrinks with the value array.
  const tempIdsRef = useRef([]);
  const nextIdRef = useRef(1);
  while (tempIdsRef.current.length < value.length) {
    tempIdsRef.current.push(`row-${nextIdRef.current++}`);
  }
  if (tempIdsRef.current.length > value.length) {
    tempIdsRef.current.length = value.length;
  }

  const productsById = useMemo(() => {
    const map = {};
    for (const p of knownProducts) {
      if (p?.id != null) map[p.id] = p;
    }
    return { ...map, ...pickedProducts };
  }, [knownProducts, pickedProducts]);

  const rows = useMemo(
    () =>
      value.map((c, idx) => ({
        id: tempIdsRef.current[idx],
        _idx: idx,
        component_id: c.id ?? null,
        quantity: c.quantity ?? 0,
      })),

    [value],
  );

  // Append a "ghost" row used as the inline add affordance. Filtered out of
  // any business logic that reads `value` since it lives only in the grid.
  const GHOST_ID = '__ghost_add__';
  const gridRows = useMemo(() => [...rows, { id: GHOST_ID, _ghost: true }], [rows]);

  const selectedProductIds = useMemo(() => new Set(value.map(c => c.id).filter(Boolean)), [value]);

  const updateAt = useCallback(
    (idx, patch) => {
      const next = value.map((c, i) => (i === idx ? { ...c, ...patch } : c));
      onChange(next);
    },
    [value, onChange],
  );

  const removeAt = useCallback(
    idx => {
      tempIdsRef.current.splice(idx, 1);
      onChange(value.filter((_c, i) => i !== idx));
    },
    [value, onChange],
  );

  const appendRow = useCallback(() => {
    tempIdsRef.current.push(`row-${nextIdRef.current++}`);
    onChange([...value, { id: null, quantity: 1 }]);
    // Focus the new row's Component cell so users can pick immediately.
    requestAnimationFrame(() => {
      const newId = tempIdsRef.current[tempIdsRef.current.length - 1];
      apiRef.current?.startCellEditMode?.({ id: newId, field: 'component_id' });
    });
  }, [value, onChange, apiRef]);

  const handleProcessRowUpdate = useCallback(
    newRow => {
      if (newRow._ghost) return newRow;
      const idx = newRow._idx;
      updateAt(idx, {
        id: newRow.component_id ?? null,
        quantity: Number(newRow.quantity) || 0,
      });
      return newRow;
    },
    [updateAt],
  );

  const columns = useMemo(
    () => [
      {
        field: 'component_id',
        headerName: t('warehouse.componentMatrix.component'),
        flex: 1,
        minWidth: 280,
        editable: true,
        display: 'flex',
        sortable: false,
        valueGetter: (val, row) => row.component_id,
        renderCell: params => {
          if (params.row._ghost) {
            return (
              <Box
                onClick={appendRow}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  height: '100%',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Add fontSize="small" />
                <Typography variant="body2">{t('warehouse.componentMatrix.addComponent')}</Typography>
              </Box>
            );
          }
          const product = productsById[params.value];
          if (!product) {
            return (
              <Typography variant="body2" color="text.disabled">
                {t('warehouse.componentMatrix.pickAProduct')}
              </Typography>
            );
          }
          return (
            <Box sx={{ overflow: 'hidden' }}>
              <Typography variant="body2">
                <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 0.75 }}>
                  {product.item_part_num}:
                </Box>
                {product.item_name}
              </Typography>
              {product.item_description && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {product.item_description}
                </Typography>
              )}
            </Box>
          );
        },
        renderEditCell: params => {
          const apiRef = params.api;
          return (
            <CellAutocomplete
              loadOptions={loadOptions}
              getOptionLabel={opt => `${opt.item_part_num}: ${opt.item_name}`}
              renderOptionRow={(option, inputValue) => {
                const searchWords = [inputValue?.trim()].filter(Boolean);
                return (
                  <>
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontFamily: 'monospace', color: 'text.secondary', mr: 0.75 }}>
                        <Highlighter
                          searchWords={searchWords}
                          autoEscape
                          textToHighlight={`${option.item_part_num}:`}
                        />
                      </Box>
                      <Highlighter searchWords={searchWords} autoEscape textToHighlight={option.item_name} />
                    </Typography>
                    {option.item_description && (
                      <Typography variant="caption" color="text.secondary">
                        <Highlighter searchWords={searchWords} autoEscape textToHighlight={option.item_description} />
                      </Typography>
                    )}
                  </>
                );
              }}
              disableOption={opt => opt.id !== params.value && selectedProductIds.has(opt.id)}
              onSelect={picked => {
                if (picked) {
                  setPickedProducts(prev => (prev[picked.id] ? prev : { ...prev, [picked.id]: picked }));
                }
                apiRef.setEditCellValue({ id: params.id, field: params.field, value: picked?.id ?? null });
                apiRef.stopCellEditMode({ id: params.id, field: params.field });
              }}
              onCancel={() => apiRef.stopCellEditMode({ id: params.id, field: params.field })}
              placeholder={t('warehouse.componentMatrix.searchProducts')}
            />
          );
        },
      },
      {
        field: 'quantity',
        headerName: t('warehouse.componentMatrix.quantity'),
        width: 130,
        editable: true,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        display: 'flex',
        sortable: false,
        renderCell: params => {
          if (params.row._ghost) return null;
          return <Box sx={{ fontFamily: 'monospace' }}>{params.value}</Box>;
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        display: 'flex',
        renderCell: params => {
          if (params.row._ghost) return null;
          return (
            <IconButton
              size="small"
              onClick={() => removeAt(params.row._idx)}
              aria-label={t('warehouse.componentMatrix.removeComponent')}
            >
              <Delete fontSize="small" />
            </IconButton>
          );
        },
      },
    ],
    [productsById, loadOptions, selectedProductIds, appendRow, removeAt, t],
  );

  const isCellEditable = useCallback(params => !params.row._ghost, []);

  return (
    <DataGridPro
      apiRef={apiRef}
      rows={gridRows}
      columns={columns}
      autoHeight
      hideFooter
      disableRowSelectionOnClick
      disableColumnFilter
      processRowUpdate={handleProcessRowUpdate}
      isCellEditable={isCellEditable}
      onCellDoubleClick={(params, event) => {
        if (params.row._ghost) event.defaultMuiPrevented = true;
      }}
      sx={{
        '& .MuiDataGrid-row.ghost-row .MuiDataGrid-cell': { borderBottom: 'none' },
      }}
      getRowClassName={params => (params.row._ghost ? 'ghost-row' : '')}
      localeText={{ noRowsLabel: t('warehouse.componentMatrix.noComponentsYet') }}
    />
  );
}

export default ComponentMatrix;
